import { logError } from "../../../medonc/utils/customLogger";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { FirestoreEnvironment } from "../../enums/firestoreEnvironment";
import { getBearerToken } from "./bearerToken";

export interface CopyDocumentsByPathPayload {
  document_paths: string[];
  source_environment: FirestoreEnvironment;
  target_environment: FirestoreEnvironment;
  target_base_path?: string | null;
}

export interface CopyDocumentsByPathResponse {
  success: boolean;
  message?: string;
  copied_count?: number;
  not_found_count?: number;
  not_found_paths?: string[];
  source_project?: string;
  target_project?: string;
  error?: string | null;
}

/**
 * Copies Firestore documents between environments (e.g., prod to dev).
 * Used for data migration. Requires bearer token authentication.
 *
 * @param payload - Document paths and environment configuration
 * @returns Promise with success status, message, and copied document count
 */
export const copyDocumentsByPath = async (
  payload: CopyDocumentsByPathPayload,
): Promise<CopyDocumentsByPathResponse> => {
  try {
    const token = await getBearerToken();

    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.COPY_DOCUMENTS_BY_PATH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logError(
        new Error(
          `Copy documents API call failed: ${response.status} ${response.statusText}`,
        ),
        JSON.stringify(errorData),
      );
      return {
        success: false,
        message:
          errorData.message ||
          `Request failed with status ${response.status}: ${response.statusText}`,
      };
    }

    const responseData = await response.json();
    return {
      success: responseData.success ?? true,
      message: responseData.message,
      copied_count: responseData.copied_count ?? payload.document_paths.length,
      not_found_count: responseData.not_found_count,
      not_found_paths: responseData.not_found_paths,
      source_project: responseData.source_project,
      target_project: responseData.target_project,
      error: responseData.error,
    };
  } catch (error) {
    logError(error as Error, "Error in copyDocumentsByPath");
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
